/**
 * Centralized Query Options for TanStack Query
 *
 * Reusable, type-safe query definitions for consistent data fetching.
 * These options can be used in route loaders (SSR prefetch) and components.
 *
 * @see Phase 4: Query options pattern from styling plan
 */

import { orpc } from "@/utils/orpc";

// ============================================
// Public Content Queries (No Auth Required)
// ============================================

/**
 * Featured projects for homepage
 * Stale time: 5 minutes (projects change infrequently)
 */
export const featuredProjectsQueryOptions = () =>
	orpc.getFeaturedProjects.queryOptions();

/**
 * Public portfolio statistics (project count, years experience)
 * Stale time: 5 minutes
 */
export const publicStatsQueryOptions = () => orpc.getPublicStats.queryOptions();

/**
 * Single project by ID
 * Used by project detail modal
 */
export const projectByIdQueryOptions = (id: string) =>
	orpc.getProjectById.queryOptions({ input: { id } });

/**
 * List projects with pagination and filtering
 * Used by portfolio page infinite scroll
 */
export const listProjectsQueryOptions = (options?: {
	category?: "residential" | "commercial" | "hospitality";
	search?: string;
	sort?: "newest" | "oldest" | "a-z" | "z-a" | "featured";
	limit?: number;
	offset?: number;
}) =>
	orpc.listProjects.queryOptions({
		input: {
			category: options?.category,
			search: options?.search,
			sort: options?.sort ?? "newest",
			limit: options?.limit ?? 12,
			offset: options?.offset ?? 0,
		},
	});

/**
 * Response type for list projects query (wrapped response format)
 */
type ListProjectsResponse = {
	success: true;
	data: Array<{
		id: string;
		title: string;
		short_description: string | null;
		category: "residential" | "commercial" | "hospitality";
		location: string | null;
		year: number | null;
		images: Array<{
			id: string;
			url: string;
			thumbnail_url: string;
			alt: string;
			order: number;
		}>;
		is_featured: boolean;
		status: "draft" | "published" | "archived";
	}>;
	pagination: {
		total: number;
		hasMore: boolean;
		page?: number;
		limit?: number;
	};
};

/**
 * Infinite query for portfolio page with proper caching
 * Handles pagination state automatically, survives navigation
 */
export const listProjectsInfiniteQueryOptions = (options?: {
	category?: "residential" | "commercial" | "hospitality";
	search?: string;
	sort?: "newest" | "oldest" | "a-z" | "z-a" | "featured";
	limit?: number;
}) =>
	orpc.listProjects.infiniteOptions({
		input: (pageParam = 0) => ({
			category: options?.category,
			search: options?.search,
			sort: options?.sort ?? "newest",
			limit: options?.limit ?? 12,
			offset: pageParam,
		}),
		initialPageParam: 0,
		getNextPageParam: (
			lastPage: ListProjectsResponse,
			_allPages: ListProjectsResponse[],
			lastPageParam: number,
		) =>
			lastPage.pagination.hasMore
				? lastPageParam + (options?.limit ?? 12)
				: undefined,
	});

/**
 * Batch project lookup by IDs
 * Used by Puck components with external fields
 */
export const projectsByIdsQueryOptions = (ids: string[]) =>
	orpc.getProjectsByIds.queryOptions({ input: { ids } });

// ============================================
// CMS Content Queries
// ============================================

/**
 * FAQ items with optional category filter
 * Stale time: 30 minutes (FAQ rarely changes)
 */
export const faqQueryOptions = (
	category?: "general" | "projects" | "pricing" | "process" | "services",
) =>
	orpc.getFAQs.queryOptions({
		input: category ? { category } : undefined,
	});

/**
 * Services with optional featured/projectType filter
 * Stale time: 30 minutes
 */
export const servicesQueryOptions = (options?: {
	featured?: boolean;
	projectType?: "residential" | "commercial" | "hospitality";
}) =>
	orpc.getServices.queryOptions({
		input: options
			? { featured: options.featured, projectType: options.projectType }
			: undefined,
	});

/**
 * Site settings by key
 * Stale time: 1 hour (settings rarely change)
 */
export const siteSettingsQueryOptions = (
	key:
		| "footer"
		| "about"
		| "brand"
		| "homepage"
		| "navigation"
		| "headerNavigation"
		| "contact"
		| "legal"
		| "credentials"
		| "featureFlags"
		| "mediaFolders"
		| "socialLinks",
) => orpc.getSiteSettings.queryOptions({ input: { key } });

/**
 * All public site settings in one call
 * Used by footer, about page, etc.
 */
export const allSiteSettingsQueryOptions = () =>
	orpc.getAllSiteSettings.queryOptions();

/**
 * Feature flags for experimental features
 * Stale time: 5 minutes
 */
export const featureFlagsQueryOptions = () =>
	orpc.getFeatureFlags.queryOptions();

// ============================================
// Testimonials
// ============================================

/**
 * All published testimonials
 * Used by testimonials page/section
 */
export const allTestimonialsQueryOptions = () =>
	orpc.getTestimonials.queryOptions();

/**
 * Featured testimonials for homepage carousel
 */
export const featuredTestimonialsQueryOptions = () =>
	orpc.getFeaturedTestimonials.queryOptions();

/**
 * Batch testimonial lookup by IDs
 * Used by Puck components
 */
export const testimonialsByIdsQueryOptions = (ids: string[]) =>
	orpc.getTestimonialsByIds.queryOptions({ input: { ids } });

// ============================================
// Pages (Puck Visual Editor)
// ============================================

/**
 * Get page content by slug for rendering
 */
export const pageBySlugQueryOptions = (slug: string) =>
	orpc.getPageBySlug.queryOptions({ input: { slug } });

/**
 * List all pages for admin
 */
export const listPagesQueryOptions = () => orpc.listPages.queryOptions();

// ============================================
// Media
// ============================================

/**
 * List media items for Puck media picker
 * @param page - Page number (1-indexed)
 * @param limit - Items per page
 * @param folder - Filter by folder (projects, logos, avatars, documents)
 */
export const listMediaQueryOptions = (options?: {
	page?: number;
	limit?: number;
	folder?: "projects" | "logos" | "avatars" | "documents";
}) =>
	orpc.listMedia.queryOptions({
		input: {
			page: options?.page ?? 1,
			limit: options?.limit ?? 20,
			folder: options?.folder,
		},
	});

// ============================================
// Client Portal Queries (Auth Required)
// ============================================

/**
 * Client's projects in portal dashboard
 */
export const portalProjectsQueryOptions = () =>
	orpc.portalGetMyProjects.queryOptions();

/**
 * Single project detail in client portal
 */
export const portalProjectDetailQueryOptions = (clientProjectId: string) =>
	orpc.portalGetProjectDetails.queryOptions({ input: { clientProjectId } });

/**
 * Project files in client portal
 */
export const portalProjectFilesQueryOptions = (clientProjectId: string) =>
	orpc.portalGetProjectFiles.queryOptions({ input: { clientProjectId } });

/**
 * Client's invoices in portal
 */
export const portalInvoicesQueryOptions = () =>
	orpc.portalGetMyInvoices.queryOptions();

/**
 * Invoice detail in portal
 */
export const portalInvoiceDetailQueryOptions = (invoiceId: string) =>
	orpc.portalGetInvoiceDetails.queryOptions({ input: { invoiceId } });

/**
 * Client's profile in portal
 */
export const portalProfileQueryOptions = () =>
	orpc.portalGetMyProfile.queryOptions();
